import React, { useEffect, useMemo, useRef, useState } from "react";
import { Group, Text, Tooltip } from "@mantine/core";

import { OverflownText } from "../OverflownText";
import { RELEASE_EMPTY_LABEL } from "../../constants/releases";
import { ReleaseOption } from "../../utils/releaseOptions";

interface ReleaseChipsProps {
  /**
   * Ids exactly as stored on whichever owner they came from — an idea or a
   * product item: ObjectId strings, unresolved. Ids with no matching release
   * render as a muted placeholder.
   */
  releaseIds: string[];
  /** Every release the account has, used to resolve ids to name and colour. */
  options: ReleaseOption[];
  /** Column width when rendered in the ideas table; unset elsewhere. */
  columnWidth?: number;
  /** Overrides the `Select release` placeholder. */
  emptyLabel?: string;
  /** Muted rendering — used for a set this surface cannot edit. */
  dimmed?: boolean;
  /**
   * Ids to mute individually, within one strip. Lets a single chip row carry two sets — an
   * idea's own releases and its delivery item's — without splitting them into two boxes,
   * which keeps the `+N` overflow and the tooltip counting the whole set.
   */
  dimmedIds?: string[];
  /** Appended to a dimmed release's name in the tooltip, so the muted styling has a reason. */
  dimmedLabelSuffix?: string;
}

const CHIP_MAX_WIDTH = 100;

/**
 * The chip strip from `Tags-old/TagMenu`, kept to the same visual language
 * (10%-alpha background of the chip's own colour, 4px radius, `+N` overflow)
 * but driven by releases rather than tags. It is presentation only: the menu
 * that edits the selection lives in `ReleaseAssignmentMenu`.
 */
export const ReleaseChips: React.FC<ReleaseChipsProps> = ({
  releaseIds,
  options,
  columnWidth,
  emptyLabel = RELEASE_EMPTY_LABEL,
  dimmed = false,
  dimmedIds,
  dimmedLabelSuffix,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dimmedSet = useMemo(
    () => new Set((dimmedIds || []).map((id) => String(id).toLowerCase())),
    [dimmedIds]
  );
  const [overflowCount, setOverflowCount] = useState<number>(0);
  const [visibleCount, setVisibleCount] = useState<number>(0);

  /**
   * The ideas-list column accessor `productItem.releaseIds` delivers a bare
   * **ObjectId array** — there is no server-side join to names, colours or
   * groups — so resolution happens here, against the `GET /releases` list.
   *
   * An id that resolves to nothing is rendered as a muted placeholder rather
   * than dropped. Dropping it would be a quiet lie in a column whose write is
   * a *full replacement*: the user would see three chips, save, and get three
   * back where the row had four. Deleting a release does cascade a `$pull`
   * server-side, so in practice this means a stale row, not a lost release —
   * and it stays visible until the list refetches.
   */
  const selected = useMemo(() => {
    const byId = new Map(
      options.map((option) => [option.value.toLowerCase(), option])
    );
    return (releaseIds || []).map((id) => {
      const resolved = byId.get(String(id).toLowerCase());
      if (resolved) return resolved;
      return {
        value: String(id),
        label: "Unknown release",
        unresolved: true,
      } as unknown as ReleaseOption & { unresolved?: boolean };
    });
  }, [releaseIds, options]);

  useEffect(() => {
    const measure = () => {
      const containerWidth =
        columnWidth || containerRef.current?.offsetWidth || 0;

      if (!containerWidth) {
        setVisibleCount(selected.length);
        setOverflowCount(0);
        return;
      }

      let used = 0;
      let visible = 0;
      selected.forEach((release, index) => {
        const maxChipWidth =
          selected.length === 1 && columnWidth
            ? columnWidth - 30
            : CHIP_MAX_WIDTH;
        const chipWidth = Math.min(
          release.label.length * 8 + 24,
          maxChipWidth
        );
        // The first chip always renders, however narrow the column is —
        // showing "+1" and nothing else would be worse than an ellipsis.
        if (index === 0 || used + chipWidth <= containerWidth - 30) {
          visible += 1;
          used += chipWidth;
        }
      });

      setVisibleCount(visible);
      setOverflowCount(selected.length - visible);
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [selected, columnWidth]);

  /**
   * Every connected release, not just the visible chips — the strip truncates
   * to `+N` at any width, and the header pill is narrow enough that it usually
   * does. Unresolved ids carry their id so a stale row is identifiable.
   */
  const allReleasesLabel = useMemo(
    () =>
      selected
        .map((release) => {
          const name = (release as any).unresolved
            ? `Unknown release (${release.value})`
            : release.label;
          const isDimmed = dimmedSet.has(String(release.value).toLowerCase());
          return isDimmed && dimmedLabelSuffix ? `${name} ${dimmedLabelSuffix}` : name;
        })
        .join(", "),
    [selected, dimmedSet, dimmedLabelSuffix]
  );

  // Only worth a tooltip once there is more than one release: a lone chip is
  // either fully visible or already covered by `OverflownText`'s own tooltip.
  const showAllReleasesTooltip = selected.length > 1;

  const strip = (
    <Group
      ref={containerRef}
      spacing={6}
      noWrap
      style={{
        flexWrap: "nowrap",
        overflow: "hidden",
        width: "min-content",
        opacity: dimmed ? 0.6 : 1,
      }}
    >
      {selected.slice(0, Math.max(visibleCount, 1)).map((release) => {
        const maxWidth =
          selected.length === 1 && columnWidth
            ? columnWidth - 30
            : CHIP_MAX_WIDTH;
        const unresolved = !!(release as any).unresolved;
        const chipDimmed = dimmedSet.has(String(release.value).toLowerCase());
        // One neutral chip for every release: colour was removed with the picker dot and the
        // settings swatch, so there is nothing per-release left to tint with.
        const color = "#585C68";
        return (
          <OverflownText
            key={release.value}
            size="sm"
            disableTooltip={showAllReleasesTooltip}
            overTooltip={
              unresolved
                ? `This release no longer exists (${release.value})`
                : undefined
            }
            data-testid={unresolved ? "release-chip-unknown" : "release-chip"}
            data-release-dimmed={chipDimmed ? "true" : undefined}
            style={{
              backgroundColor: color === "#585C68" ? "#EFEFFD" : `${color}20`,
              color,
              padding: "0px 8px",
              borderRadius: "4px",
              display: "inline-block",
              maxWidth,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              border: "1px solid transparent",
              opacity: chipDimmed ? 0.55 : 1,
            }}
          >
            {release.label}
          </OverflownText>
        );
      })}

      {overflowCount > 0 && (
        <Text
          size="sm"
          data-testid="release-chip-overflow"
          style={{ color: "#5C5CEB", marginLeft: 4, whiteSpace: "nowrap" }}
        >
          +{overflowCount}
        </Text>
      )}

      {selected.length === 0 && (
        <Text
          w="max-content"
          size="sm"
          color="#aeaeae"
          lineClamp={1}
          px={3}
          data-testid="release-empty"
          sx={{ border: "1px solid transparent", borderRadius: 4 }}
        >
          {emptyLabel}
        </Text>
      )}
    </Group>
  );

  if (!showAllReleasesTooltip) return strip;

  return (
    <Tooltip
      label={allReleasesLabel}
      multiline
      withinPortal
      maw={400}
      data-testid="release-chips-tooltip"
    >
      {strip}
    </Tooltip>
  );
};

export default ReleaseChips;
